import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useOrganizationStore } from '@/store/pinia/organization'
import { useCatalogRepositoryStore } from '@/store/pinia/organization/catalogRepository'
import { ObjWithPrivateProps } from '@/utils/classes'
import { makeResetStoreAction, makeSetErrorsAction, makeClearErrorsAction, makeStartFetchAction, makeStopFetchAction } from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'

const DESCRIPTION_LIMIT = 200

/**
 * Key of a stack version's config in `state.configs`: one entry per component, version and use case.
 * @param {Object} params
 * @param {string} params.envCanonical
 * @param {string} params.componentCanonical
 * @param {number|string} params.versionId
 * @param {string} [params.useCase]
 * @returns {string}
 */
export function stackConfigKey ({ envCanonical, componentCanonical, versionId, useCase }) {
  return [envCanonical, componentCanonical, Number(versionId), useCase ?? ''].join(':')
}

/**
 * Wrap the use cases that carry a cloud provider so their `_cloudProvider` is available.
 * @param {Object} theConfig
 * @returns {Object}
 */
function normalizeStackConfig (theConfig) {
  const config = _.cloneDeep(theConfig)

  for (const [key, useCase] of _.entries(config)) {
    if (!_.has(useCase, 'cloud_provider')) continue

    config[key] = new ObjWithPrivateProps(useCase, {
      _cloudProvider: {
        canonical: useCase.cloud_provider,
      },
    })
  }

  return config
}

// In-flight FETCH_STACK_CONFIG calls by key, so concurrent callers of the same key share one request
const isStackConfigInProgress = (entry) => entry.status === 'loading' || entry.refreshing
const stackConfigRequests = new Map()
let lastStackConfigRequestId = 0

export const state = () => ({
  detail: null,
  config: null,
  // Stack configs by `stackConfigKey`, each loaded on its own: see FETCH_STACK_CONFIG
  configs: {},
  usecases: null,
  focusedVersionType: 'tag',
  errors: {
    stackConfig: [],
    stack: [],
    usecases: [],
  },
  fetchInProgress: {
    stack: false,
    stackConfig: false,
    usecases: false,
  },
  searchTerm: '',
})

export const useStackStore = defineStore('stack', {
  state,
  getters: {
    stack: (state) => state.detail,
    stackConfig: (state) => state.config,
    stackDescriptionLimit: () => DESCRIPTION_LIMIT,
    stackBelongsToCurrentOrg: (state) => {
      const orgStore = useOrganizationStore()
      return state.detail?.organization_canonical === orgStore.orgCanonical
    },
  },
  actions: {
    /**
     * Clear the current errors list.
     */
    CLEAR_STACK_ERRORS: makeClearErrorsAction(state),

    /**
     * Reset the whole module state to its initial shape.
     */
    RESET_STACK_STATE: makeResetStoreAction(state),

    /**
     * Replace the current errors array.
     */
    SET_ERRORS: makeSetErrorsAction(),

    /**
     * Start fetch operation.
     */
    START_FETCH: makeStartFetchAction(),

    /**
     * Stop fetch operation.
     */
    STOP_FETCH: makeStopFetchAction(),

    /**
     * Set search term.
     * @param {string} value
     * @returns {void}
     */
    SET_SEARCH (value) {
      this.searchTerm = value
    },

    /**
     * Set the focused version type for stack-version related components (tag|branch|all)
     * @param {string} value
     * @returns {void}
     */
    SET_FOCUSED_VERSION_TYPE (value) {
      this.focusedVersionType = value
    },

    /**
     * Set stack in state.
     * @param {Object} stack
     * @returns {void}
     */
    SET_STACK (stack) {
      stack.description = stack.description.substring(0, DESCRIPTION_LIMIT)
      this.detail = stack
    },

    /**
     * Set stack config in state.
     * @param {Object} params
     * @param {Object} params.config
     * @returns {void}
     */
    SET_STACK_CONFIG ({ config: theConfig }) {
      this.config = normalizeStackConfig(theConfig)
    },

    /**
     * Drop the entries of `configs`, e.g. once the stack versions were refreshed.
     * @param {Object} [options]
     * @param {string[]} [options.except] - keys of the entries, and their requests, to keep
     * @returns {void}
     */
    CLEAR_STACK_CONFIGS ({ except = [] } = {}) {
      for (const key of stackConfigRequests.keys()) {
        if (!except.includes(key)) stackConfigRequests.delete(key)
      }
      this.configs = _.pick(this.configs, except)
      this.fetchInProgress.stackConfig = _.some(this.configs, isStackConfigInProgress)
    },

    /**
     * Load the stack config of one version of a component, and optionally the component config
     * saved for that version, into `configs[stackConfigKey(...)]`.
     *
     * Each version has its own entry, so responses never overwrite each other: a late response
     * only fills the entry of its own version, and a page reads only the entry of the version
     * selected. An entry is `loaded` once both requests succeeded. Concurrent calls for the same
     * key share one request; a loaded entry is reused unless `force` is set, and a failed one is
     * fetched again.
     *
     * A forced fetch of a loaded entry keeps it `loaded`, flagged `refreshing`, until the response
     * arrives, so the forms built from it stay mounted. If that fetch fails, the loaded entry is kept
     * and the failed result is only returned.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {string} params.componentCanonical
     * @param {{ id: number, commit_hash?: string }} params.version
     * @param {string} [params.useCase] - sent as the `use_case` filter when set
     * @param {boolean} [params.withComponentConfig] - also load the component config of the version
     * @param {boolean} [params.force] - fetch again even if the entry is loaded
     * @returns {Promise<{ status: 'loading'|'loaded'|'error', config: Object|null, componentConfig: Object|null, errors: Array, componentConfigErrors: Array }>}
     */
    async FETCH_STACK_CONFIG ({ envCanonical, componentCanonical, version, useCase, withComponentConfig = false, force = false }) {
      const orgStore = useOrganizationStore()
      // Read the scope before any await: leaving the page resets the stores while a request is in
      // flight, and the later requests must not use the reset values (ENG-225)
      const { orgCanonical, projectCanonical } = orgStore
      const key = stackConfigKey({ envCanonical, componentCanonical, versionId: version.id, useCase })

      const existing = this.configs[key]
      if (!force && existing?.status === 'loaded') return existing
      const pending = stackConfigRequests.get(key)
      // Reused only while its entry is still there: a store reset drops entries, not requests
      if (!force && pending && existing?.requestId === pending.requestId) return pending.promise

      const requestId = ++lastStackConfigRequestId
      const keepShowing = force && existing?.status === 'loaded'
      this.configs[key] = keepShowing
        ? { ...existing, refreshing: true, requestId }
        : { status: 'loading', config: null, componentConfig: null, errors: [], componentConfigErrors: [], requestId }
      this.fetchInProgress.stackConfig = true

      const promise = (async () => {
        const entry = { status: 'loaded', config: null, componentConfig: null, errors: [], componentConfigErrors: [], requestId }
        try {
          const stackConfigQuery = {
            use_case: useCase,
            service_catalog_source_version_commit_hash: version.commit_hash,
            service_catalog_source_version_id: version.id,
          }
          const { data, errors } = (await this.$cycloid.ydAPI.getComponentStackConfiguration(
            orgCanonical,
            projectCanonical,
            envCanonical,
            componentCanonical,
            stackConfigQuery,
          )) || {}

          if (_.isEmpty(errors)) {
            entry.config = normalizeStackConfig(data || {})
          } else {
            entry.status = 'error'
            entry.errors = errors
          }

          if (entry.status === 'loaded' && withComponentConfig) {
            const { data: componentConfig, errors: componentConfigErrors } = (await this.$cycloid.ydAPI.getComponentConfig(
              orgCanonical,
              projectCanonical,
              envCanonical,
              componentCanonical,
              { service_catalog_source_version_id: version.id },
            )) || {}

            if (_.isEmpty(componentConfigErrors)) {
              entry.componentConfig = componentConfig || {}
            } else {
              entry.status = 'error'
              entry.componentConfigErrors = componentConfigErrors
            }
          }
        } catch (error) {
          entry.status = 'error'
          entry.errors = [{ code: 'Error', message: error?.message || String(error) }]
        }

        const isCurrent = this.configs[key]?.requestId === requestId
        if (isCurrent) {
          // A failed refresh keeps the entry loaded before it, the failure is only returned
          this.configs[key] = keepShowing && entry.status === 'error'
            ? { ..._.omit(this.configs[key], 'refreshing'), requestId }
            : entry
        }
        if (stackConfigRequests.get(key)?.requestId === requestId) stackConfigRequests.delete(key)
        this.fetchInProgress.stackConfig = _.some(this.configs, isStackConfigInProgress)

        // A forced refetch replaced this request: resolve with its result instead
        if (!isCurrent && stackConfigRequests.has(key)) return stackConfigRequests.get(key).promise
        return entry
      })()

      stackConfigRequests.set(key, { requestId, promise })
      return promise
    },

    /**
     * Set stack use cases in state.
     * @param {Object} usecases
     * @returns {void}
     */
    SET_STACK_USECASES (usecases) {
      this.usecases = usecases
    },

    /**
     * Get stack by stackRef.
     * @param {Object} params
     * @param {string} params.stackRef
     * @returns {Promise<void>}
     */
    async GET_STACK ({ stackRef }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()
      const catalogRepositoryStore = useCatalogRepositoryStore()

      this.START_FETCH('stack')
      this.CLEAR_STACK_ERRORS()

      const { data, errors } = (await this.$cycloid.ydAPI.getServiceCatalog(stackRef, orgStore.orgCanonical)) || {}

      if (data) {
        this.SET_STACK(data)
        const catalogRepositoryCanonical = data?.service_catalog_source_canonical
        const canGetCatalogRepository = this.$cycloid.permissions.canDisplay('GetServiceCatalogSource', catalogRepositoryCanonical)
        if (catalogRepositoryCanonical && canGetCatalogRepository) {
          await catalogRepositoryStore.GET_CATALOG_REPOSITORY({ catalogRepositoryCanonical })
        }
      }

      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })

      this.STOP_FETCH('stack')
    },

    /**
     * Get stack config.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {string} params.componentCanonical
     * @param {Object} params.queryParams
     * @returns {Promise<boolean>}
     */
    async GET_STACK_CONFIG ({ envCanonical, componentCanonical, queryParams }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_STACK_ERRORS('stackConfig')
      this.START_FETCH('stackConfig')

      const { data, errors } = (await this.$cycloid.ydAPI.getComponentStackConfiguration(
        orgStore.orgCanonical,
        orgStore.projectCanonical,
        envCanonical,
        componentCanonical,
        queryParams,
      )) || {}

      if (data) this.SET_STACK_CONFIG({ config: data })
      if (errors) {
        this.SET_ERRORS({ key: 'stackConfig', errors })
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
      }

      this.STOP_FETCH('stackConfig')
      return !_.isEmpty(data)
    },

    /**
     * Get stack usecases.
     * @param {Object} params
     * @param {string} params.stackRef
     * @param {Object} params.queryParams
     * @returns {Promise<void>}
     */
    async GET_STACK_USECASES ({ stackRef, queryParams }) {
      const orgStore = useOrganizationStore()

      this.CLEAR_STACK_ERRORS('usecases')
      this.START_FETCH('usecases')

      const { data, errors } = await this.$cycloid.ydAPI.getServiceCatalogTemplateUseCases(orgStore.orgCanonical, stackRef, queryParams) || {}

      if (data) this.SET_STACK_USECASES(data)
      if (errors) this.SET_ERRORS({ key: 'usecases', errors })

      this.STOP_FETCH('usecases')
    },

    /**
     * Create stack from template.
     * @param {Object} params
     * @param {Object} params.stack
     * @param {VueRouter} params.$router
     * @returns {Promise<void>}
     */
    async CREATE_STACK_FROM_TEMPLATE ({ stack, $router }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_STACK_ERRORS()

      const { data, errors } = (await this.$cycloid.ydAPI.createServiceCatalogFromTemplate(orgStore.orgCanonical, stack)) || {}

      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })

      if (data) {
        $router.push({ name: 'stack', params: { stackRef: data.ref } })
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.stacks.created') })
      }
    },

    /**
     * Update stack.
     * @param {Object} params
     * @param {Object} params.stack
     * @returns {Promise<boolean>}
     */
    async UPDATE_STACK ({ stack }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_STACK_ERRORS()

      const { data, errors } = (await this.$cycloid.ydAPI.updateServiceCatalog(orgStore.orgCanonical, stack.ref, stack)) || {}

      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'stack', errors })
      }

      if (data) {
        this.SET_STACK(stack)
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.stacks.updated', { stackName: stack.name }) })
      }

      return _.isEmpty(errors)
    },

    /**
     * Validate a stack's dependencies against a project and environment.
     * Returns the canonicals of the dependency stacks that are not instantiated
     * yet (empty when every dependency is met).
     * @param {Object} params
     * @param {string} params.stackRef
     * @param {string} params.projectCanonical
     * @param {string} params.environmentCanonical
     * @returns {Promise<string[]>}
     */
    async VALIDATE_STACK_DEPENDENCIES ({ stackRef, projectCanonical, environmentCanonical }) {
      const orgStore = useOrganizationStore()

      const { data } = (await this.$cycloid.ydAPI.validateServiceCatalogDependencies(
        stackRef,
        orgStore.orgCanonical,
        projectCanonical,
        environmentCanonical,
      )) || {}

      return _.get(data, 'unmet_dependencies', [])
    },

    /**
     * Delete stack.
     * @param {Object} params
     * @param {Object} params.stack
     * @param {VueRouter} params.$router
     * @returns {Promise<void>}
     */
    async DELETE_STACK ({ stack, $router }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_STACK_ERRORS()

      const { errors } = (await this.$cycloid.ydAPI.deleteServiceCatalog(orgStore.orgCanonical, stack.ref)) || {}

      if (errors) return this.SET_ERRORS({ key: 'stack', errors })

      $router.push({ name: 'stacks' })
      this.RESET_STACK_STATE('detail')
      alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.stacks.deleted', { stackName: stack.name }) })
    },
  },
})
